<div class="container mt-4">
  <div class="row">
    <div class="col-12">
      <!-- Page Header -->
      <div class="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2>Excel Upload Demo</h2>
          <p class="text-muted mb-0">Test the drag-and-drop Excel upload functionality</p>
        </div>
        <button class="btn btn-outline-primary" (click)="downloadTemplate()">
          <i class="fas fa-download me-2"></i>
          Download Sample Template
        </button>
      </div>

      <!-- Upload Section -->
      <div class="row mb-5">
        <div class="col-lg-8 offset-lg-2">
          <div class="card shadow-sm">
            <div class="card-header">
              <h5 class="mb-0">
                <i class="fas fa-upload me-2"></i>
                Upload Excel File
              </h5>
            </div>
            <div class="card-body">
              <app-excel-upload
                acceptedTypes=".xlsx,.xls,.csv"
                [maxFileSize]="10485760"
                [allowMultiple]="false"
                [disabled]="false"
                (fileSelected)="onFileSelected($event)"
                (uploadProgress)="onUploadProgress($event)"
                (uploadComplete)="onUploadComplete($event)"
                (uploadError)="onUploadError($event)">
              </app-excel-upload>
            </div>
          </div>
        </div>
      </div>

      <!-- Current Progress Section -->
      <div *ngIf="currentProgress" class="row mb-4">
        <div class="col-lg-8 offset-lg-2">
          <div class="card border-warning">
            <div class="card-header bg-warning bg-opacity-10">
              <h6 class="mb-0">
                <i class="fas fa-spinner fa-spin me-2"></i>
                Processing Upload
              </h6>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-6">
                  <strong>Stage:</strong> {{ currentProgress.stage | titlecase }}
                </div>
                <div class="col-md-6">
                  <strong>Progress:</strong> {{ currentProgress.percentage.toFixed(0) }}%
                </div>
              </div>
              <div class="mt-2">
                <strong>Message:</strong> {{ currentProgress.message }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Upload History Section -->
      <div *ngIf="uploadHistory.length > 0" class="row">
        <div class="col-12">
          <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center">
              <h5 class="mb-0">
                <i class="fas fa-history me-2"></i>
                Upload History
              </h5>
              <div class="btn-group">
                <span class="badge bg-primary me-2">{{ uploadHistory.length }} uploads</span>
                <button class="btn btn-outline-danger btn-sm" (click)="clearHistory()">
                  <i class="fas fa-trash me-1"></i>
                  Clear History
                </button>
              </div>
            </div>
            <div class="card-body p-0">
              <div class="table-responsive">
                <table class="table table-hover mb-0">
                  <thead class="table-light">
                    <tr>
                      <th style="width: 40px;">#</th>
                      <th>Filename</th>
                      <th class="text-center">Rows</th>
                      <th class="text-center">Columns</th>
                      <th>Sample Headers</th>
                      <th class="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr *ngFor="let upload of uploadHistory; let i = index">
                      <td class="text-muted">{{ i + 1 }}</td>
                      <td>
                        <div class="d-flex align-items-center">
                          <i class="fas fa-file-excel text-success me-2"></i>
                          <div>
                            <div class="fw-semibold">{{ upload.filename }}</div>
                            <small class="text-muted">{{ formatTimestamp() }}</small>
                          </div>
                        </div>
                      </td>
                      <td class="text-center">
                        <span class="badge bg-info">{{ upload.rowCount.toLocaleString() }}</span>
                      </td>
                      <td class="text-center">
                        <span class="badge bg-secondary">{{ upload.headers.length }}</span>
                      </td>
                      <td>
                        <div class="text-truncate" style="max-width: 200px;" [title]="upload.headers.slice(0, 5).join(', ')">
                          {{ upload.headers.slice(0, 3).join(', ') }}
                          <span *ngIf="upload.headers.length > 3" class="text-muted">
                            +{{ upload.headers.length - 3 }} more
                          </span>
                        </div>
                      </td>
                      <td class="text-center">
                        <div class="btn-group btn-group-sm">
                          <button 
                            class="btn btn-outline-primary" 
                            data-bs-toggle="modal" 
                            [attr.data-bs-target]="'#dataModal' + i"
                            type="button">
                            <i class="fas fa-eye"></i>
                          </button>
                          <button class="btn btn-outline-success" type="button">
                            <i class="fas fa-download"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div *ngIf="uploadHistory.length === 0 && !currentProgress" class="row">
        <div class="col-lg-6 offset-lg-3 text-center">
          <div class="py-5">
            <i class="fas fa-file-upload fa-4x text-muted mb-3"></i>
            <h4 class="text-muted">No files uploaded yet</h4>
            <p class="text-muted">Upload an Excel file to get started with testing the functionality.</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Data Preview Modals -->
<div *ngFor="let upload of uploadHistory; let i = index">
  <div class="modal fade" [id]="'dataModal' + i" tabindex="-1">
    <div class="modal-dialog modal-xl">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="fas fa-table me-2"></i>
            {{ upload.filename }} - Data Preview
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>
        <div class="modal-body p-0">
          <div class="table-responsive" style="max-height: 400px;">
            <table class="table table-sm table-bordered mb-0">
              <thead class="table-light sticky-top">
                <tr>
                  <th class="text-center" style="width: 50px;">#</th>
                  <th *ngFor="let header of upload.headers; let idx = index">{{ header || 'Column ' + (idx + 1) }}</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let row of upload.data.slice(0, 20); let rowIndex = index">
                  <td class="text-center text-muted">{{ rowIndex + 1 }}</td>
                  <td *ngFor="let cell of row">{{ cell || '-' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div *ngIf="upload.rowCount > 20" class="p-3 bg-light text-center">
            <small class="text-muted">
              <i class="fas fa-info-circle me-1"></i>
              Showing first 20 rows of {{ upload.rowCount.toLocaleString() }} total rows
            </small>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
          <button type="button" class="btn btn-primary">
            <i class="fas fa-download me-1"></i>
            Export Data
          </button>
        </div>
      </div>
    </div>
  </div>
</div>